<script>
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();

  let oldPassword = '';
  let newPassword = '';
  let confirmPassword = '';
  let error = '';
  let success = '';
  let loading = false;

  function validate() {
    if (!oldPassword || !newPassword || !confirmPassword) {
      error = 'All fields are required';
      return false;
    }
    if (newPassword.length < 8) {
      error = 'New password must be at least 8 characters';
      return false;
    }
    if (newPassword !== confirmPassword) {
      error = 'New password and confirmation do not match';
      return false;
    }
    if (oldPassword === newPassword) {
      error = 'New password cannot be the same as old password';
      return false;
    }
    return true;
  }

  async function handleSubmit() {
    error = '';
    success = '';

    if (!validate()) return;

    loading = true;
    dispatch('change', { oldPassword, newPassword, confirmPassword });
  }

  export function setError(msg) {
    error = msg;
    loading = false;
  }

  export function setSuccess(msg) {
    success = msg;
    loading = false;
    oldPassword = '';
    newPassword = '';
    confirmPassword = '';
  }
</script>

<div class="password-card card">
  <div class="card-title">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
    Change Master Password
  </div>

  <form class="password-form" on:submit|preventDefault={handleSubmit} id="password-change-form">
    <div class="form-group">
      <label for="old-password">Current Password</label>
      <input
        id="old-password"
        type="password"
        bind:value={oldPassword}
        placeholder="Enter current password"
        autocomplete="current-password"
      />
    </div>

    <div class="form-group">
      <label for="new-password">New Password</label>
      <input
        id="new-password"
        type="password"
        bind:value={newPassword}
        placeholder="Minimum 8 characters"
        autocomplete="new-password"
      />
      {#if newPassword.length > 0 && newPassword.length < 8}
        <span class="field-hint warn">Too short ({newPassword.length}/8)</span>
      {:else if newPassword.length >= 8}
        <span class="field-hint ok">Strong ✓</span>
      {/if}
    </div>

    <div class="form-group">
      <label for="confirm-password">Confirm New Password</label>
      <input
        id="confirm-password"
        type="password"
        bind:value={confirmPassword}
        placeholder="Re-enter new password"
        autocomplete="new-password"
      />
      {#if confirmPassword.length > 0 && confirmPassword !== newPassword}
        <span class="field-hint warn">Passwords don't match</span>
      {:else if confirmPassword.length > 0 && confirmPassword === newPassword}
        <span class="field-hint ok">Match ✓</span>
      {/if}
    </div>

    {#if error}
      <div class="form-error">{error}</div>
    {/if}

    {#if success}
      <div class="form-success">{success}</div>
    {/if}

    <button type="submit" class="btn-primary submit-btn" disabled={loading} id="change-password-btn">
      {#if loading}
        <div class="btn-spinner"></div>
        Changing...
      {:else}
        Change Password
      {/if}
    </button>
  </form>
</div>

<style>
  .password-card {
    animation: fadeInUp 0.4s ease 0.1s both;
    max-width: 480px;
  }

  .password-card .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .password-card .card-title svg {
    color: var(--neon-orange);
  }

  .password-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .form-group input {
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .form-group input::placeholder {
    color: var(--text-tertiary);
  }

  .form-group input:focus {
    border-color: var(--neon-blue);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: rgba(255, 255, 255, 0.06);
  }

  .field-hint {
    font-size: 11px;
    font-weight: 500;
  }

  .field-hint.warn { color: var(--neon-orange); }
  .field-hint.ok { color: var(--neon-green); }

  .form-error {
    padding: 10px 14px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: var(--radius-sm);
    color: var(--neon-red);
    font-size: 13px;
    font-weight: 500;
  }

  .form-success {
    padding: 10px 14px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: var(--radius-sm);
    color: var(--neon-green);
    font-size: 13px;
    font-weight: 500;
  }

  .submit-btn {
    margin-top: 4px;
    justify-content: center;
  }

  .submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(59, 130, 246, 0.3);
    border-top-color: var(--neon-blue);
    border-radius: 50%;
    animation: spinSlow 0.6s linear infinite;
  }
</style>
